import { stringIncludesInsensitiveAccents } from '../../utils/normalizeForSearch';

/**
 * @typedef {object} ColumnFilterUnit
 * @property {ApiManagerTableFiltersColumnFilters[]} columns - Contiguous columns (single or same groupId)
 */

/**
 * Generate table columns hidden groups from API columns
 * @param {ApiManagerTableFiltersColumnFilters[]} columnFilters - The column filters
 * @param {string} search - The search string
 * @returns {ApiManagerTableFiltersColumnFilters[]} The table columns hidden groups
 */
export function searchInColumnFilters(columnFilters, search = '') {
  if (!columnFilters) return [];

  const searchTrimmed = (search || '').trim();
  if (!searchTrimmed) return columnFilters;

  return columnFilters.filter((columnFilter) => {
    const libMatch = stringIncludesInsensitiveAccents(columnFilter.lib, searchTrimmed);
    const optGroupMatch = stringIncludesInsensitiveAccents(columnFilter.optGroup, searchTrimmed);
    return libMatch || optGroupMatch;
  });
}

/**
 * Partition column filters into adjacent units: same non-null groupId → one unit; otherwise one column per unit.
 * Assumes API returns group columns contiguously.
 * @param {ApiManagerTableFiltersColumnFilters[]} columnFilters
 * @returns {ColumnFilterUnit[]}
 */
export function partitionColumnFiltersIntoAdjacentUnits(columnFilters) {
  if (!Array.isArray(columnFilters) || columnFilters.length === 0) return [];

  /** @type {ColumnFilterUnit[]} */
  const units = [];
  let i = 0;
  while (i < columnFilters.length) {
    const cf = columnFilters[i];
    const gid = cf.groupId;
    if (gid == null || gid === '') {
      units.push({ columns: [cf] });
      i += 1;
    } else {
      const cols = [cf];
      let j = i + 1;
      while (j < columnFilters.length && columnFilters[j].groupId === gid) {
        cols.push(columnFilters[j]);
        j += 1;
      }
      units.push({ columns: cols });
      i = j;
    }
  }
  return units;
}

/**
 * True when every column shares the same non-empty groupId (same rules as
 * {@link partitionColumnFiltersIntoAdjacentUnits} for grouping).
 * @param {ApiManagerTableFiltersColumnFilters[] | null | undefined} columnFilters
 * @returns {boolean}
 */
export function areColumnFiltersSameNonEmptyGroupId(columnFilters) {
  if (!Array.isArray(columnFilters) || columnFilters.length === 0) return false;
  const first = columnFilters[0].groupId;
  if (first == null || first === '') return false;
  return columnFilters.every((c) => c.groupId === first);
}

/**
 * @param {ColumnFilterUnit[]} units
 * @returns {ApiManagerTableFiltersColumnFilters[]}
 */
export function flattenColumnFilterUnits(units) {
  return units.flatMap((u) => u.columns);
}

/**
 * Stable id for @dnd-kit/sortable (first column id in the unit).
 * @param {ColumnFilterUnit} unit
 * @returns {string}
 */
export function getSortableIdForColumnFilterUnit(unit) {
  return `unit-${unit.columns[0].id}`;
}

/**
 * @param {unknown[]} arr
 * @param {number} from
 * @param {number} to
 * @returns {unknown[]}
 */
export function arrayMoveImmutable(arr, from, to) {
  const next = [...arr];
  const [removed] = next.splice(from, 1);
  next.splice(to, 0, removed);
  return next;
}

/**
 * Replace visible slots in allUnits with units from reorderedVisibleUnits in order.
 * @param {ColumnFilterUnit[]} allUnits
 * @param {ColumnFilterUnit[]} reorderedVisibleUnits
 * @returns {ColumnFilterUnit[]}
 */
export function mergeVisibleColumnFilterUnitOrder(allUnits, reorderedVisibleUnits) {
  let v = 0;
  return allUnits.map((u) => {
    if (u.columns.some((c) => c.visible)) {
      const next = reorderedVisibleUnits[v];
      v += 1;
      return next;
    }
    return u;
  });
}

/**
 * @param {ColumnFilterUnit[]} allUnits
 * @param {ColumnFilterUnit[]} visibleUnits
 * @param {number} oldIndex
 * @param {number} newIndex
 * @returns {ColumnFilterUnit[]}
 */
export function reorderVisibleColumnFilterUnits(allUnits, visibleUnits, oldIndex, newIndex) {
  const reorderedVisible = /** @type {ColumnFilterUnit[]} */ (
    arrayMoveImmutable(visibleUnits, oldIndex, newIndex)
  );
  return mergeVisibleColumnFilterUnitOrder(allUnits, reorderedVisible);
}
